import { useCallback, useEffect, useState } from "react";

import { Button } from "../components/Button";
import {
  deleteGuestNote,
  listGuestNotes,
  saveGuestNote,
} from "../lib/notesStorage";
import { formatIrkutskDateTime } from "../lib/time";

function formatUpdated(iso) {
  return formatIrkutskDateTime(iso);
}

export function GuestNotes({ onBack }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storageError, setStorageError] = useState("");
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState({ id: null, title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setStorageError("");
    listGuestNotes()
      .then((rows) => setNotes(Array.isArray(rows) ? rows : []))
      .catch((err) => {
        setStorageError(err?.message || "Не удалось открыть хранилище");
        setNotes([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openNew = () => {
    setEditing({ id: null, title: "", body: "" });
    setMode("edit");
  };

  const openEdit = (note) => {
    setEditing({ id: note.id, title: note.title, body: note.body });
    setMode("edit");
  };

  const backToList = () => {
    setMode("list");
    setEditing({ id: null, title: "", body: "" });
    refresh();
  };

  const onSave = async () => {
    setSaving(true);
    setStorageError("");
    try {
      await saveGuestNote({
        id: editing.id || undefined,
        title: editing.title,
        body: editing.body,
      });
      backToList();
    } catch (err) {
      setStorageError(err?.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!editing.id) return;
    if (!window.confirm("Удалить эту заметку?")) return;
    setSaving(true);
    setStorageError("");
    try {
      await deleteGuestNote(editing.id);
      backToList();
    } catch (err) {
      setStorageError(err?.message || "Не удалось удалить");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="client">
      <header className="sectionHeader">
        <h2>Заметки</h2>
        <p>Только на этом устройстве, не передаются на сервер. Можешь записать миксы, которые тебе понравились, и вообще всё, что твоей душе угодно.</p>
      </header>

      <div className="eventsToolbar">
        <Button onClick={mode === "edit" ? backToList : onBack}>
          {mode === "edit" ? "К списку" : "Назад"}
        </Button>
      </div>

      {storageError ? <p className="errorMessage">{storageError}</p> : null}

      {mode === "list" ? (
        <>
          {loading ? <p className="muted">Загрузка...</p> : null}
          {!loading && notes.length === 0 ? (
            <div className="homeSurface guestNotes__empty">
              <p className="guestNotes__emptyText">Пока нет заметок.</p>
              <Button variant="accent" type="button" onClick={openNew}>
                Новая заметка
              </Button>
            </div>
          ) : null}
          {!loading && notes.length > 0 ? (
            <div className="guestNotes__listWrap">
              <div className="guestNotes__toolbar">
                <Button variant="accent" type="button" onClick={openNew}>
                  Новая заметка
                </Button>
              </div>
              <ul className="guestNotes__list">
                {notes.map((note) => (
                  <li key={note.id}>
                    <button
                      type="button"
                      className="card card--light card--clickable guestNotes__card"
                      onClick={() => openEdit(note)}
                    >
                      <span className="card__title">{note.title}</span>
                      <span className="card__subtitle guestNotes__date">
                        {formatUpdated(note.updatedAt)}
                      </span>
                      <span className="guestNotes__preview">
                        {(note.body || "").slice(0, 120)}
                        {(note.body || "").length > 120 ? "…" : ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <div className="homeSurface guestNotes__editor">
          <label className="field">
            Заголовок
            <input
              type="text"
              value={editing.title}
              onChange={(e) =>
                setEditing((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Название"
              maxLength={200}
            />
          </label>
          <label className="field">
            Текст
            <textarea
              value={editing.body}
              onChange={(e) =>
                setEditing((prev) => ({ ...prev, body: e.target.value }))
              }
              placeholder="Ваша заметка…"
              rows={12}
              className="guestNotes__textarea"
            />
          </label>
          <div className="guestNotes__actions">
            <Button
              variant="accent"
              type="button"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? "Сохранение…" : "Сохранить"}
            </Button>
            {editing.id ? (
              <Button type="button" onClick={onDelete} disabled={saving}>
                Удалить
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
